import React, {useState} from "react";
import {Button, Card, Checkbox, Form, InputNumber, Row, Select, theme} from "antd";
import {observer} from "mobx-react";
import {useRequiredStore} from "../../../../../utils/store";
import {AgentDataStoreContext} from "../../agent.data.store";
import {useTranslate} from "../../../../../hooks/translate.hook";
import {RootStoreContext} from "../../../../../stores/root/root.store";
import {accountService} from "../../../../../services/accounts/account.service";
import {notifications} from "../../../../../services/notifications/notifications";

export const AgentDataAgentAccount: React.FC = observer(() => {
    const {account, loadData} = useRequiredStore(AgentDataStoreContext);
    const {commonStore} = useRequiredStore(RootStoreContext);
    const t = useTranslate();
    const {token} = theme.useToken();
    const [loading, setLoading] = useState<boolean>(false);
    const [form] = Form.useForm();

    if (!account) {
        return <></>;
    }

    const submit = async (values: any) => {
        setLoading(true);
        try {
            await accountService.updateGeneral(account.id, values);
            await loadData();
            notifications.successfully();
        } catch (e: any) {
            if (e.response?.data.fields) {
                form.setFields(e.response.data.fields);
            }
        } finally {
            setLoading(false);
        }
    };

    const defaultValues = {
        legalType: account.legalType,
        language: account.language,
        bank: account.bank?.id,
        orderType: account.orderType,
        searchByVINLimit: account.searchByVINLimit,
        searchByVINRemainingLimit: account.searchByVINRemainingLimit,
        allowMultipleWarehousesOrder: account.allowMultipleWarehousesOrder,
    };

    return (
        <Form
            size="middle"
            form={form}
            layout="vertical"
            name="agent_account_general_form"
            initialValues={defaultValues}
            onFinish={async (values) => {
                await form.validateFields();
                await submit(values);
            }}
        >
            <Card
                size="small"
                title={t("CLIENT.ACCOUNT_DETAILS", {defaultValue: "Account details"})}
                style={{width: "100%"}}
                styles={{
                    header: {
                        color: token.colorTextSecondary,
                        height: 40,
                    },
                    body: {
                        paddingTop: token.paddingLG,
                    },
                }}
            >
                <Form.Item
                    name="legalType"
                    label={t("CLIENT.TYPE")}
                    rules={[
                        {
                            required: true,
                            message: t("FROM.ERROR.PLEASE_SELECT_VALUE"),
                        },
                    ]}
                >
                    <Select
                        disabled={loading}
                        placeholder={t("CLIENT.TYPE")}
                        showSearch
                        allowClear
                        optionFilterProp="children"
                    >
                        {commonStore.agentTypes.map((item) => (
                            <Select.Option key={item} value={item}>
                                {item.beautify()}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>
                <Form.Item
                    name="language"
                    label={t("LOCALES")}
                    rules={[
                        {
                            required: true,
                            message: t("FROM.ERROR.PLEASE_SELECT_VALUE"),
                        },
                    ]}
                >
                    <Select placeholder={t("LOCALES")} disabled={loading}>
                        <Select.Option value="en">EN</Select.Option>
                        <Select.Option value="bg">BG</Select.Option>
                        <Select.Option value="sr">SR</Select.Option>
                        <Select.Option value="de">DE</Select.Option>
                    </Select>
                </Form.Item>
                <Form.Item
                    name="bank"
                    label={t("BANK.NAME")}
                    rules={[
                        {
                            required: true,
                            message: t("FROM.ERROR.PLEASE_ENTER_VALUE"),
                        },
                    ]}
                >
                    <Select
                        showSearch
                        optionFilterProp="children"
                        disabled={loading}
                        placeholder={t("BANK.NAME")}
                    >
                        {commonStore.banks.map((bank) => (
                            <Select.Option key={bank.id} value={bank.id}>
                                {bank.visibleName}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>
                <Form.Item
                    name="orderType"
                    label={t("CLIENT.ORDER_TYPE.TITLE")}
                    rules={[
                        {
                            required: true,
                            message: t("FROM.ERROR.PLEASE_ENTER_VALUE"),
                        },
                    ]}
                >
                    <Select
                        showSearch
                        allowClear
                        optionFilterProp="children"
                        disabled={loading}
                        placeholder={t("CLIENT.ORDER_TYPE.TITLE")}
                    >
                        {commonStore.orderTypes.map((item) => (
                            <Select.Option value={item} key={item}>
                                {t(`CLIENT.ORDER_TYPE.${item.toUpperCase()}`)}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>
                <Form.Item
                    name="searchByVINLimit"
                    label={t("BRANCH.SEARCH_BY_VIN_LIMIT")}
                    rules={[
                        {
                            required: true,
                            message: t("FROM.ERROR.PLEASE_ENTER_VALUE"),
                        },
                    ]}
                >
                    <InputNumber disabled={loading} style={{width: "100%"}} />
                </Form.Item>
                <Form.Item
                    name="searchByVINRemainingLimit"
                    label={t("CLIENT.SEARCH_BY_VIN_REMAINING_LIMIT")}
                    rules={[
                        {
                            required: true,
                            message: t("FROM.ERROR.PLEASE_ENTER_VALUE"),
                        },
                    ]}
                >
                    <InputNumber disabled={loading} style={{width: "100%"}} />
                </Form.Item>
                <Form.Item name="allowMultipleWarehousesOrder" valuePropName="checked">
                    <Checkbox disabled={loading}>
                        {t("CLIENT.ALLOW_MULTIPLE_WAREHOUSE_ORDER")}
                    </Checkbox>
                </Form.Item>
                <Row justify="end" style={{marginTop: 8}}>
                    <Button loading={loading} type="primary" onClick={() => form.submit()}>
                        {t("SAVE")}
                    </Button>
                </Row>
            </Card>
        </Form>
    );
});
